/**
 * Typing Challenge game logic.
 * A 60-second timed round with short prompts. Score = correct prompts × points.
 * No lives; the round ends when the timer expires.
 */

import { makeRng, pickWord, DIFFICULTY_MULTIPLIERS } from '../random';

const ROUND_DURATION_MS = 60_000;
const BASE_POINTS = 15;

/**
 * @typedef {Object} ChallengePrompt
 * @property {string} text
 * @property {number} position
 * @property {number} incorrectChars
 */

/**
 * @typedef {Object} TypingChallengeState
 * @property {'running' | 'ended'} phase
 * @property {number} seed
 * @property {'easy'|'medium'|'hard'} difficulty
 * @property {ReturnType<typeof makeRng>} rng
 * @property {ChallengePrompt} prompt
 * @property {number} score
 * @property {number} correctChars
 * @property {number} incorrectChars
 * @property {number} correctWords
 * @property {number} startedAtMs
 * @property {number} elapsedMs
 * @property {string} endReason
 */

/**
 * @param {{ seed?: number, difficulty?: 'easy'|'medium'|'hard' }} opts
 */
export function createGame({ seed = 1, difficulty = 'medium' } = {}) {
  const rng = makeRng(seed);
  return {
    phase: 'running',
    seed,
    difficulty,
    rng,
    prompt: { text: pickWord(rng), position: 0, incorrectChars: 0 },
    score: 0,
    correctChars: 0,
    incorrectChars: 0,
    correctWords: 0,
    startedAtMs: 0,
    elapsedMs: 0,
    endReason: null,
  };
}

/**
 * @param {TypingChallengeState} state
 * @param {number} nowMs
 */
export function tick(state, nowMs) {
  if (state.phase !== 'running') return state;
  if (state.startedAtMs === 0) {
    return { ...state, startedAtMs: nowMs, elapsedMs: 0 };
  }
  const elapsed = nowMs - state.startedAtMs;
  if (elapsed >= ROUND_DURATION_MS) {
    return {
      ...state,
      phase: 'ended',
      elapsedMs: ROUND_DURATION_MS,
      endReason: 'time',
    };
  }
  return { ...state, elapsedMs: elapsed };
}

/**
 * @param {TypingChallengeState} state
 * @param {string} char
 */
export function typeChar(state, char) {
  if (state.phase !== 'running') return state;
  const expected = state.prompt.text[state.prompt.position];
  const correct = char === expected;

  if (!correct) {
    return {
      ...state,
      incorrectChars: state.incorrectChars + 1,
      prompt: { ...state.prompt, incorrectChars: state.prompt.incorrectChars + 1 },
    };
  }

  const nextPos = state.prompt.position + 1;
  const wordComplete = nextPos >= state.prompt.text.length;

  if (wordComplete) {
    const wordPoints = BASE_POINTS * DIFFICULTY_MULTIPLIERS[state.difficulty];
    const penalty = state.prompt.incorrectChars * 2;
    const earned = Math.max(0, Math.round(wordPoints - penalty));
    return {
      ...state,
      score: state.score + earned,
      correctChars: state.correctChars + state.prompt.text.length,
      correctWords: state.correctWords + 1,
      prompt: {
        text: pickWord(state.rng),
        position: 0,
        incorrectChars: 0,
      },
    };
  }

  return {
    ...state,
    correctChars: state.correctChars + 1,
    prompt: { ...state.prompt, position: nextPos },
  };
}

export const TYPING_CHALLENGE_ROUND_MS = ROUND_DURATION_MS;